import React, { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, GitCompare, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DiffList({ title, items, render, testid }) {
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-800 p-3 min-w-0" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">{title} ({items.length})</p>
      <ul className="space-y-1 max-h-56 overflow-y-auto text-sm">
        {items.map((it, i) => <li key={i} className="text-slate-700 dark:text-slate-300 break-words">{render(it)}</li>)}
        {items.length === 0 && <li className="text-slate-400 text-xs">Ninguno</li>}
      </ul>
    </div>
  );
}

export const ProveedoresSyncPanel = ({ onSynced }) => {
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);

  const compare = async () => {
    setBusy(true);
    try { setDiff((await axios.get(`${API}/flujo-sync/proveedores/compare`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo comparar'); }
    finally { setBusy(false); }
  };

  const sync = async () => {
    if (!window.confirm('Se crearán en cada sistema los proveedores que falten (por NIT, sin duplicar). ¿Continuar?')) return;
    setBusy(true);
    try {
      const r = (await axios.post(`${API}/flujo-sync/proveedores/sync`)).data;
      toast.success(`Sincronizando en segundo plano: ${r.a_crear_en_flujo} al Flujo y ${r.a_crear_en_portal} al Portal. Presiona "Comparar" en unos minutos.`, { duration: 7000 });
      onSynced && onSynced();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo sincronizar'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="proveedores-sync-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
        <h2 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-[#2c57c5]" /> Proveedores (por NIT)
        </h2>
        <div className="sm:ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={compare} disabled={busy} data-testid="compare-providers-btn">
            <GitCompare className="h-4 w-4 mr-2" /> Comparar
          </Button>
          <Button size="sm" onClick={sync} disabled={busy} className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white" data-testid="sync-providers-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${busy ? 'animate-spin' : ''}`} /> Sincronizar proveedores
          </Button>
        </div>
      </div>
      {!diff ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">Presiona "Comparar" para ver qué proveedores faltan en cada sistema.</p>
      ) : (
        <>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3" data-testid="providers-match-count">{diff.coinciden} proveedores coinciden por NIT.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <DiffList title="Solo en Portal (se crearán en Flujo)" items={diff.solo_portal} testid="solo-portal-list" render={(p) => `${p.nombre} · ${p.nit}`} />
            <DiffList title="Solo en Flujo (se crearán en Portal)" items={diff.solo_flujo} testid="solo-flujo-list" render={(p) => `${p.nombre} · ${p.nit}`} />
            <DiffList title="Mismo NIT, nombre distinto (revisar)" items={diff.nombre_distinto} testid="nombre-distinto-list"
              render={(p) => <><span className="font-medium">{p.nit}</span>: {p.portal} <span className="text-slate-400">vs</span> {p.flujo}</>} />
          </div>
        </>
      )}
    </Card>
  );
};
